import { useState, useEffect } from 'react';
import { useNavigate, Link, Routes, Route, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Package, Tag, Upload, ImageIcon, FileText, Star, LogOut, Menu, X,
  ShoppingCart, LineChart, Link2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ProductsAdmin from '../components/admin/ProductsAdmin';
import CategoriesAdmin from '../components/admin/CategoriesAdmin';
import BulkUploadAdmin from '../components/admin/BulkUploadAdmin';
import CarouselAdmin from '../components/admin/CarouselAdmin';
import FAQAdmin from '../components/admin/FAQAdmin';
import ReviewsAdmin from '../components/admin/ReviewsAdmin';
import DashboardHome from '../components/admin/DashboardHome';
import OrdersAdmin from '../components/admin/OrdersAdmin';
import SalesAdmin from '../components/admin/SalesAdmin';
import SiteLinksAdmin from '../components/admin/SiteLinksAdmin';
import TermsAdmin from '../components/admin/TermsAdmin';
import PrivacyAdmin from '../components/admin/PrivacyAdmin';
import { authAPI } from '../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');

  // 3-state route guard:
  //   'checking' — we have a token in localStorage but haven't validated it server-side yet
  //   'valid'    — backend confirmed via /auth/me; safe to render dashboard
  //   'invalid'  — no token, OR backend rejected it; redirecting to /admin/login
  const [authState, setAuthState] = useState(() =>
    authAPI.isAuthenticated() ? 'checking' : 'invalid'
  );

  useEffect(() => {
    if (authState === 'invalid') {
      authAPI.logout();
      navigate('/admin/login', { replace: true });
      return;
    }
    if (authState !== 'checking') return;
    let cancelled = false;
    authAPI.me()
      .then((d) => { if (!cancelled) { setEmail(d.email); setAuthState('valid'); } })
      .catch(() => { if (!cancelled) setAuthState('invalid'); });
    return () => { cancelled = true; };
  }, [authState, navigate]);

  if (authState === 'invalid') return null;
  if (authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="admin-auth-loading">
        <div className="text-sm text-gray-500">Verifying session…</div>
      </div>
    );
  }

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login', { replace: true });
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: ShoppingCart, label: 'Orders', path: '/admin/dashboard/orders' },
    { icon: LineChart, label: 'Sales', path: '/admin/dashboard/sales' },
    { icon: Package, label: 'Products', path: '/admin/dashboard/products' },
    { icon: Tag, label: 'Categories', path: '/admin/dashboard/categories' },
    { icon: Upload, label: 'Bulk Upload', path: '/admin/dashboard/bulk-upload' },
    { icon: ImageIcon, label: 'Hero Carousel', path: '/admin/dashboard/carousel' },
    { icon: FileText, label: 'FAQ Content', path: '/admin/dashboard/faq' },
    { icon: Star, label: 'Reviews', path: '/admin/dashboard/reviews' },
    { icon: Link2, label: 'Site Links', path: '/admin/dashboard/site-links' },
    { icon: FileText, label: 'Terms of Service', path: '/admin/dashboard/terms' },
    { icon: FileText, label: 'Privacy Policy', path: '/admin/dashboard/privacy' },
  ];

  const isActive = (path) => {
    if (path === '/admin/dashboard') return location.pathname === '/admin/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden"
                aria-label="Toggle menu"
                data-testid="admin-mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X /> : <Menu />}
              </button>
              <h1 className="text-xl font-bold text-blue-600">BlueSpark Admin</h1>
              {email && <span className="hidden md:inline text-sm text-gray-500">· {email}</span>}
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm" data-testid="admin-view-site-btn">View Site</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          <aside className={`${mobileMenuOpen ? 'block' : 'hidden'} lg:block w-64 flex-shrink-0`}>
            <Card>
              <CardContent className="p-4">
                <nav className="space-y-1">
                  {menuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive(item.path)
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-blue-50 text-gray-700'
                      }`}
                      data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </aside>

          <main className="flex-1 min-w-0">
            <Routes>
              <Route path="/" element={<DashboardHome />} />
              <Route path="/orders" element={<OrdersAdmin />} />
              <Route path="/sales" element={<SalesAdmin />} />
              <Route path="/products" element={<ProductsAdmin />} />
              <Route path="/categories" element={<CategoriesAdmin />} />
              <Route path="/bulk-upload" element={<BulkUploadAdmin />} />
              <Route path="/prices" element={<BulkUploadAdmin />} />
              <Route path="/carousel" element={<CarouselAdmin />} />
              <Route path="/faq" element={<FAQAdmin />} />
              <Route path="/reviews" element={<ReviewsAdmin />} />
              <Route path="/site-links" element={<SiteLinksAdmin />} />
              <Route path="/terms" element={<TermsAdmin />} />
              <Route path="/privacy" element={<PrivacyAdmin />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

